import {useCallback, useEffect, useRef, useState} from 'react'
import {useLazyResolveUserNamesQuery} from '@apis/infrastructureTenants'
import type {Message} from '@interfaces/alpinist/message'
import type {UserContextProps} from '@containers/App/UserProvider'

interface UseActorLabelsArgs {
  messages: Message[]
  showAuthors: boolean
  currentUser: UserContextProps['currentUser']
}

interface UseActorLabelsResult {
  getActorLabel: (message: Message) => string | null
  isActorLoading: (message: Message) => boolean
  resetActorLabels: () => void
}

export const useActorLabels = ({
  messages,
  showAuthors,
  currentUser,
}: UseActorLabelsArgs): UseActorLabelsResult => {
  // Ids already sent to the organizations lookup (in flight or answered). An id
  // the lookup does not return (not a member of the current organization) stays
  // here too, so it is not asked for again on every message update.
  const requestedLookupRef = useRef<Set<string>>(new Set())
  const [resolvedLabels, setResolvedLabels] = useState<Record<string, string>>({})
  const [loadingLabels, setLoadingLabels] = useState<Record<string, boolean>>({})

  const [resolveUserNames] = useLazyResolveUserNamesQuery()
  const currentIdentityUuid = currentUser?.userprofile?.identity_uuid

  const resetActorLabels = useCallback(() => {
    requestedLookupRef.current = new Set()
    setResolvedLabels({})
    setLoadingLabels({})
  }, [])

  const getActorLabel = useCallback(
    (message: Message): string | null => {
      if (message.role !== 'user') return null

      const actorId = message.user_id

      if (currentIdentityUuid && actorId && String(currentIdentityUuid) === String(actorId)) {
        return 'You'
      }

      if (!actorId) return null
      return resolvedLabels[String(actorId)] || null
    },
    [currentIdentityUuid, resolvedLabels],
  )

  const isActorLoading = useCallback(
    (message: Message): boolean => {
      if (message.role !== 'user' || !message.user_id) return false
      return Boolean(loadingLabels[String(message.user_id)])
    },
    [loadingLabels],
  )

  useEffect(() => {
    if (!showAuthors) return

    // Every author not yet looked up, minus yourself (labelled "You" without a
    // request), resolved together in one batched request.
    const ids = Array.from(
      new Set(
        messages
          .filter((message) => message.role === 'user' && message.user_id)
          .map((message) => String(message.user_id)),
      ),
    )
      .filter(
        (id) =>
          !resolvedLabels[id] &&
          !requestedLookupRef.current.has(id) &&
          id !== String(currentIdentityUuid ?? ''),
      )
      .sort()

    if (!ids.length) return

    ids.forEach((id) => requestedLookupRef.current.add(id))
    setLoadingLabels((prev) => ({...prev, ...Object.fromEntries(ids.map((id) => [id, true]))}))

    // preferCacheValue: a remount (or another chat) asking for the same authors
    // reuses the RTK Query cache instead of a new round-trip.
    void resolveUserNames(ids, true)
      .unwrap()
      .then((users) => {
        const labels: Record<string, string> = {}
        users.forEach((user) => {
          const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
          if (name) {
            labels[user.identity_uuid] = name
          }
        })
        if (Object.keys(labels).length) {
          setResolvedLabels((prev) => ({...prev, ...labels}))
        }
        return users
      })
      .catch(() => {
        // A failed lookup may be retried on the next message update.
        ids.forEach((id) => requestedLookupRef.current.delete(id))
      })
      .finally(() => {
        setLoadingLabels((prev) => {
          const next = {...prev}
          ids.forEach((id) => delete next[id])
          return next
        })
      })
  }, [messages, showAuthors, currentIdentityUuid, resolveUserNames, resolvedLabels])

  return {getActorLabel, isActorLoading, resetActorLabels}
}
